<template>
  <V2AtomsBaseCard
    size="small"
    :class="card.themeClasses"
    class="max-w-full border-2"
  >
    <div :class="innerClass">
      <Component
        :is="iconComponent.icon"
        :class="{'min-h-6 min-w-6 w-6 h-6':iconSize === 'standard', 'min-h-4 min-w-4 w-4 h-4':iconSize === 'small'}"
        v-bind="iconComponent.iconProps"
      />
      <div class="flex flex-col" :class="{'gap-2':disclaimerText || okBtnText || cancelBtnText}">
        <div
          class="font-normal text-semantic-surface-on-surface my-auto"
          v-html="content"
        />
        <div class="flex items-center gap-1">
          <span
            v-if="disclaimerText"
            class="text-sm font-normal text-semantic-surface-on-surface"
            v-html="disclaimerText"></span>
          <div
            v-if="okBtnText || cancelBtnText"
            class="flex items-center gap-1"
            :class="actionsStacked ? 'flex-col w-full' : 'flex-row'">
            <V2AtomsButton
              v-if="okBtnText"
              :id="okButtonId"
              :enable-tracking="enableTracking"
              size="small"
              :full="actionsStacked"
              theme="tertiary"
              :inverted="true"
              :tracking-additional-params="okButtonTrackingAdditionalData"
              class="!text-semantic-surface-on-surface !border-transparent bg-transparent hover:!bg-transparent focus:!ring-0 font-semibold !p-0 !h-auto text-sm"
              @click="$emit('ok')"
            >
              {{ okBtnText }}
            </V2AtomsButton>
            <V2AtomsButton
              v-if="cancelBtnText"
              :id="cancelButtonId"
              :enable-tracking="enableTracking"
              size="small"
              :full="actionsStacked"
              theme="tertiary"
              class="!text-semantic-primary-variant-dark hover:!bg-transparent font-semibold"
              @click="$emit('cancel')"
            >
              {{ cancelBtnText }}
            </V2AtomsButton>
          </div>
        </div>
      </div>
    </div>
  </V2AtomsBaseCard>
</template>

<script setup>
import V2IconsInfoCircle from '~/components/v2/icons/InfoCircle.vue'
import V2IconsCancelCircle from '~/components/v2/icons/CancelCircle.vue'
import V2IconsCheckCircle from '~/components/v2/icons/CheckCircle.vue'
import V2IconsWarning from '~/components/v2/icons/Warning.vue'

defineOptions({
  name: 'V2MoleculesBanner'
})

defineEmits(['ok', 'cancel'])

const props = defineProps({
  type: {
    type: String,
    default: 'info',
    validator: value =>
      ['info', 'warning', 'error', 'success'].includes(value)
  },

  content: {
    type: String,
    default: ''
  },

  disclaimerText: {
    type: String,
    default: ''
  },

  okBtnText: {
    type: String,
    default: ''
  },

  cancelBtnText: {
    type: String,
    default: ''
  },

  actionsStacked: {
    type: Boolean,
    default: false
  },
  iconSize:{
    type: String,
    default: 'standard',
    validator: value => ['standard', 'small'].includes(value)
  },
  enableTracking:{
    type: Boolean,
    default: false
  },
  okButtonId:{
    type: String,
    default: ''
  },
  okButtonTrackingAdditionalData:{
    type: Object,
    default: () => ({})
  },
  cancelButtonId:{
    type: String,
    default: ''
  },
  innerClass: {
    type: String,
    default: 'flex gap-2'
  }
})

// Icon Component
const iconComponent = computed(() => {
  const icons = {
    info: V2IconsInfoCircle,
    error: V2IconsCancelCircle,
    warning: V2IconsWarning,
    success: V2IconsCheckCircle
  }

  const iconProps = {
    info: {
      fill: semanticColorLookup('states.info.default'),
      type: 'info-regular'
    },
    error: {
      fill: semanticColorLookup('states.error.default')
    },
    warning: {
      fill: semanticColorLookup('states.warning.default')
    },
    success: {
      fill: semanticColorLookup('states.success.default'),
      type: 'outline'
    }
  }

  const cardThemeClasses = {
    info: '!bg-semantic-states-warning-variant border-semantic-states-warning-default',
    error: '!bg-semantic-states-error-variant border-semantic-states-error-default',
    warning: '!bg-semantic-states-warning-variant border-semantic-states-warning-default',
    success: '!bg-semantic-states-success-variant border-semantic-states-success-default'
  }

  return {
    icon: icons[props.type],
    iconProps: iconProps[props.type],
    cardThemeClasses: cardThemeClasses[props.type]
  }
})

// Icon Component
const card = computed(() => {
  const themeClasses = {
    info: '!bg-semantic-states-info-variant border-semantic-states-info-default',
    error: '!bg-semantic-states-error-variant border-semantic-states-error-default',
    warning: '!bg-semantic-states-warning-variant border-semantic-states-warning-default',
    success: '!bg-semantic-states-success-variant border-semantic-states-success-default'
  }

  return {
    themeClasses: themeClasses[props.type]
  }
})
</script>
